import type { TextFieldProps } from "@mui/material/TextField"

import colors from "@constants/colors"
import {
  body16MainStyles,
  smallcapsBold13Styles,
  subtextStyles,
} from "@constants/typography"
import { ErrorMessage } from "@hookform/error-message"
import TextField from "@mui/material/TextField"
import React from "react"
import { useFormContext } from "react-hook-form"
import styled, { css } from "styled-components"

const TextInput = styled(TextField)<any>`
  ${({ error }) => css`
    width: 100%;

    .MuiInput-input {
      ${body16MainStyles}
      color: ${error ? colors.errorBackground : colors.black};
    }

    .MuiInput-underline:before {
      border-bottom: 1px solid ${colors.mediumGray};
    }

    && .MuiInput-underline:hover:before {
      border-bottom: 1px solid ${colors.black};
    }

    .MuiInput-underline:after {
      border-bottom: 1px solid ${colors.black};
    }

    .MuiInput-input::placeholder,
    .MuiInputLabel-root {
      ${body16MainStyles}
      color: ${error ? colors.errorBackground : colors.mediumGray};
      opacity: 1;
    }

    && .MuiInputLabel-shrink {
      ${smallcapsBold13Styles}
      font-size: calc(11px / 0.75);
      line-height: calc(19px / 0.75);
      color: ${error ? colors.errorBackground : colors.mediumGray};

      &.Mui-focused {
        color: ${error ? colors.errorBackground : colors.black};
      }
    }

    .MuiFormHelperText-root {
      ${subtextStyles}
      margin-top: 12px;
      padding-left: 16px;
      color: ${error ? colors.errorBackground : colors.black};
    }
  `}
`

export const RHFTextInput = React.forwardRef<HTMLInputElement, any>(
  ({ error, helperText, inputRef, label, name, ...rest }, ref) => {
    const { formState } = useFormContext?.() || { formState: { errors: {} } }
    const id = rest.id || name
    return (
      <>
        <TextField
          aria-describedby={error ? `${id}-error` : undefined}
          aria-invalid={!!error || undefined}
          error={!!error}
          id={id}
          inputRef={inputRef || ref}
          label={label}
          {...rest}
          value={rest.value ?? ""}
        />
        {helperText ?? (
          <ErrorMessage
            errors={formState?.errors || {}}
            name={name}
            render={({ message }) => (
              <div
                id={`${id}-error`}
                style={{ display: message ? "block" : "none" }}
              >
                {message}
              </div>
            )}
          />
        )}
      </>
    )
  }
)

export const RHFTextInputWithoutController = React.forwardRef<
  HTMLInputElement,
  any
>(({ error, helperText, inputRef, label, name, ...rest }, ref) => {
  // Not using FormProvider here; avoid useFormContext
  const formState = { errors: {} as Record<string, unknown> }
  const id = rest.id || name
  return (
    <>
      <TextField
        aria-describedby={error ? `${id}-error` : undefined}
        aria-invalid={!!error || undefined}
        error={!!error}
        id={id}
        inputRef={inputRef || ref}
        label={label}
        name={name}
        {...rest}
        value={rest.value ?? ""}
      />
      {helperText ?? (
        <ErrorMessage
          errors={formState.errors || {}}
          name={name}
          render={({ message }) => (
            <div
              id={`${id}-error`}
              style={{ display: message ? "block" : "none" }}
            >
              {message}
            </div>
          )}
        />
      )}
    </>
  )
})

export default (props: TextFieldProps) => <TextInput {...props} />
